import { ref } from 'vue'
import { gamificationService } from '@/services/api/gamificationService'

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'stun:stun4.l.google.com:19302' },
]

export function useWebRTC() {
  const localStream = ref(null)
  const remoteStreams = ref({})
  const connectionStates = ref({})

  let _gameId = null
  let _myId = null
  const _peers = {}
  const _pendingCandidates = {}
  let _pollTimer = null
  let _pollActive = false
  const _retryTimers = {}
  let _offerRetryTimers = {}

  function log(...args) {
    console.log('%c[WebRTC]', 'color:#00bcd4;font-weight:bold', ...args)
  }
  function warn(...args) {
    console.warn('%c[WebRTC]', 'color:#ff9800;font-weight:bold', ...args)
  }

  const _encodeSDP = (sdpObj) => {
    if (!sdpObj || !sdpObj.sdp) return sdpObj
    return { ...sdpObj, sdp: btoa(unescape(encodeURIComponent(sdpObj.sdp))), _b64: true }
  }

  const _decodeSDP = (sdpObj) => {
    if (!sdpObj || !sdpObj.sdp) return sdpObj
    if (!sdpObj._b64) return sdpObj
    try {
      const sdp = decodeURIComponent(escape(atob(sdpObj.sdp)))
      return { type: sdpObj.type, sdp }
    } catch {
      return sdpObj
    }
  }

  async function initCamera() {
    if (localStream.value) {
      const tracks = localStream.value.getVideoTracks()
      if (tracks.length > 0 && tracks[0].readyState === 'live') {
        log('Camera already active, reusing')
        return true
      }
      localStream.value.getTracks().forEach(t => t.stop())
      localStream.value = null
    }
    try {
      log('Requesting camera access...')
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 360 } },
        audio: false,
      })
      localStream.value = stream
      log('Camera OK — tracks:', stream.getVideoTracks().length)
      return true
    } catch (e) {
      warn('Camera access denied or failed:', e.name, e.message)
      return false
    }
  }

  function configure(gameId, myId) {
    _gameId = gameId
    _myId = myId
    log(`Configured: gameId=${gameId}, myId=${myId}`)
  }

  function _ensureLocalTracks(pc) {
    if (!localStream.value) return
    const senders = pc.getSenders()
    const hasVideo = senders.some(s => s.track && s.track.kind === 'video')
    if (!hasVideo) {
      localStream.value.getTracks().forEach(track => {
        pc.addTrack(track, localStream.value)
      })
      log('Late-added local tracks to PC')
    }
  }

  function _createPC(peerId, initiator) {
    if (_peers[peerId]) {
      const existing = _peers[peerId]
      const state = existing.connectionState || existing.iceConnectionState
      if (state === 'connected' || state === 'completed') return existing
      if (state === 'failed' || state === 'closed') {
        log(`Existing PC for ${peerId} is ${state}, replacing`)
        existing.close()
        delete _peers[peerId]
      } else if (state === 'new' || state === 'connecting') {
        return existing
      } else {
        existing.close()
        delete _peers[peerId]
      }
    }

    log(`Creating PC for ${peerId} (initiator=${initiator})`)
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS, iceCandidatePoolSize: 5 })

    if (localStream.value) {
      localStream.value.getTracks().forEach(track => pc.addTrack(track, localStream.value))
      log(`Added ${localStream.value.getTracks().length} local track(s) for ${peerId}`)
    }

    pc.ontrack = (event) => {
      log(`ontrack from ${peerId}: kind=${event.track.kind}`)
      if (event.streams[0]) {
        remoteStreams.value = { ...remoteStreams.value, [peerId]: event.streams[0] }
        log(`Remote stream SET for ${peerId} ✓`)
      } else if (event.track) {
        const stream = new MediaStream([event.track])
        remoteStreams.value = { ...remoteStreams.value, [peerId]: stream }
        log(`Remote stream SET (from track) for ${peerId} ✓`)
      }
    }

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        _sendSignal(peerId, 'ice', event.candidate.toJSON())
      }
    }

    pc.onicegatheringstatechange = () => {
      log(`ICE gathering (${peerId}): ${pc.iceGatheringState}`)
    }

    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState
      log(`ICE connection (${peerId}): ${state}`)
      connectionStates.value = { ...connectionStates.value, [peerId]: state }
      if (state === 'connected' || state === 'completed') {
        log(`ICE CONNECTED to ${peerId} ✓`)
        if (_offerRetryTimers[peerId]) {
          clearTimeout(_offerRetryTimers[peerId])
          delete _offerRetryTimers[peerId]
        }
      }
    }

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState
      log(`Connection (${peerId}): ${state}`)
      connectionStates.value = { ...connectionStates.value, [peerId]: state }

      if (state === 'connected') {
        log(`PEER CONNECTED: ${peerId} ✓✓✓`)
        _ensureLocalTracks(pc)
        if (_retryTimers[peerId]) {
          clearTimeout(_retryTimers[peerId])
          delete _retryTimers[peerId]
        }
      }

      if (state === 'failed') {
        warn(`Connection FAILED for ${peerId}, will retry`)
        _scheduleRetry(peerId)
      }
    }

    _peers[peerId] = pc

    if (initiator) {
      _makeOffer(pc, peerId)
    }

    return pc
  }

  function _scheduleRetry(peerId) {
    if (_retryTimers[peerId]) return
    _retryTimers[peerId] = setTimeout(() => {
      delete _retryTimers[peerId]
      if (!_pollActive) return
      log(`Retrying connection to ${peerId}...`)
      _removePeer(peerId)
      const shouldInitiate = _myId.localeCompare(peerId) > 0
      _createPC(peerId, shouldInitiate)
    }, 3000)
  }

  async function _makeOffer(pc, peerId) {
    try {
      log(`Creating offer for ${peerId}...`)
      const offer = await pc.createOffer({
        offerToReceiveVideo: true,
        offerToReceiveAudio: false,
      })
      await pc.setLocalDescription(offer)
      const encoded = _encodeSDP(pc.localDescription.toJSON())
      await _sendSignal(peerId, 'offer', encoded)
      log(`Offer sent to ${peerId} ✓ (b64, ${pc.localDescription.sdp?.length || 0} chars original)`)

      _offerRetryTimers[peerId] = setTimeout(() => {
        delete _offerRetryTimers[peerId]
        if (!_pollActive) return
        const currentPc = _peers[peerId]
        if (!currentPc) return
        const ice = currentPc.iceConnectionState
        const sig = currentPc.signalingState
        const hasStream = !!remoteStreams.value[peerId]
        if (ice === 'connected' || ice === 'completed' || hasStream) {
          log(`Peer ${peerId} is alive (ice=${ice}, stream=${hasStream}), no retry needed`)
          return
        }
        if (sig === 'have-local-offer') {
          log(`No answer from ${peerId} after 15s, retrying...`)
          _removePeer(peerId)
          const shouldInitiate = _myId.localeCompare(peerId) > 0
          _createPC(peerId, shouldInitiate)
        }
      }, 15000)
    } catch (e) {
      warn('makeOffer failed:', e.message)
    }
  }

  async function _handleSignal(signal) {
    const { from, type, data } = signal
    if (!from || !type || !data) return
    log(`← Signal: type=${type} from=${from.substring(0, 15)}...`)

    try {
      if (type === 'offer') {
        let pc = _peers[from]
        if (pc) {
          const st = pc.signalingState
          if (st !== 'stable') {
            log(`PC for ${from} in ${st}, closing for new offer`)
            pc.close()
            delete _peers[from]
            pc = null
          }
        }
        pc = pc || _createPC(from, false)

        const decoded = _decodeSDP(data)
        const isB64 = decoded !== data
        log(`Setting remote offer for ${from} (b64=${isB64}, ${decoded?.sdp?.length || 0} chars)`)
        await pc.setRemoteDescription(new RTCSessionDescription(decoded))
        log(`Remote description (offer) set for ${from} ✓`)

        await _flushCandidates(from)

        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        const encodedAnswer = _encodeSDP(pc.localDescription.toJSON())
        await _sendSignal(from, 'answer', encodedAnswer)
        log(`Answer sent to ${from} ✓ (b64)`)

      } else if (type === 'answer') {
        const pc = _peers[from]
        if (!pc) { warn(`No PC for answer from ${from}`); return }
        if (_offerRetryTimers[from]) {
          clearTimeout(_offerRetryTimers[from])
          delete _offerRetryTimers[from]
          log(`Cancelled retry timer for ${from} (answer received)`)
        }
        if (pc.signalingState === 'have-local-offer') {
          const decodedAnswer = _decodeSDP(data)
          await pc.setRemoteDescription(new RTCSessionDescription(decodedAnswer))
          log(`Remote description (answer) set for ${from} ✓`)
          await _flushCandidates(from)
        } else if (pc.signalingState === 'stable') {
          log(`Already stable for ${from}, ignoring duplicate answer`)
        } else {
          warn(`Unexpected signalingState=${pc.signalingState} for answer from ${from}`)
        }

      } else if (type === 'ice') {
        const pc = _peers[from]
        if (pc && pc.remoteDescription) {
          try { await pc.addIceCandidate(new RTCIceCandidate(data)) } catch { /* skip */ }
        } else {
          if (!_pendingCandidates[from]) _pendingCandidates[from] = []
          _pendingCandidates[from].push(data)
        }
      }
    } catch (e) {
      warn('handleSignal error:', e.message)
      if (type === 'offer' && _peers[from]) {
        log(`Offer handling failed for ${from}, cleaning up broken PC`)
        try { _peers[from].close() } catch { /* ok */ }
        delete _peers[from]
      }
    }
  }

  async function _flushCandidates(peerId) {
    const pc = _peers[peerId]
    const pending = _pendingCandidates[peerId]
    if (pc && pending && pending.length) {
      log(`Flushing ${pending.length} queued ICE for ${peerId}`)
      for (const c of pending) {
        try { await pc.addIceCandidate(new RTCIceCandidate(c)) } catch { /* skip */ }
      }
      _pendingCandidates[peerId] = []
    }
  }

  async function _sendSignal(to, type, data) {
    if (!_gameId || !_myId) return
    try {
      await gamificationService.sendWebRTCSignal(_gameId, { from: _myId, to, type, data })
    } catch (e) {
      warn(`sendSignal FAILED (${type} → ${to.substring(0, 15)}):`, e.response?.status || e.message)
    }
  }

  async function _pollSignals() {
    if (!_gameId || !_myId || !_pollActive) return
    try {
      const { data: signals } = await gamificationService.getWebRTCSignals(_gameId, _myId)
      if (signals && Array.isArray(signals) && signals.length > 0) {
        log(`Poll: received ${signals.length} signal(s)`)
        for (const sig of signals) {
          await _handleSignal(sig)
        }
      }
    } catch (e) {
      if (e.response?.status !== 404) {
        warn('pollSignals error:', e.response?.status || e.message)
      }
    }
  }

  function connectToPeer(peerId) {
    if (!_myId) return
    if (peerId === _myId) return

    if (_peers[peerId]) {
      const state = _peers[peerId].connectionState || _peers[peerId].iceConnectionState
      if (state === 'connected' || state === 'completed' || state === 'connecting' || state === 'new') return
      log(`Existing PC for ${peerId} state=${state}, recreating`)
      _removePeer(peerId)
    }
    const shouldInitiate = _myId.localeCompare(peerId) > 0
    log(`connectToPeer(${peerId.substring(0, 15)}...) initiator=${shouldInitiate}`)
    _createPC(peerId, shouldInitiate)
  }

  function _removePeer(peerId) {
    if (_peers[peerId]) {
      try { _peers[peerId].close() } catch { /* ok */ }
      delete _peers[peerId]
    }
    const streams = { ...remoteStreams.value }
    delete streams[peerId]
    remoteStreams.value = streams
    delete _pendingCandidates[peerId]
    if (_offerRetryTimers[peerId]) {
      clearTimeout(_offerRetryTimers[peerId])
      delete _offerRetryTimers[peerId]
    }
  }

  function startPolling() {
    if (_pollActive) return
    _pollActive = true
    log('Signal polling started (1s interval)')
    _pollSignals()
    _pollTimer = setInterval(_pollSignals, 1000)
  }

  function stopPolling() {
    _pollActive = false
    if (_pollTimer) {
      clearInterval(_pollTimer)
      _pollTimer = null
    }
  }

  function isPolling() {
    return _pollActive
  }

  function cleanup() {
    log('Cleanup — stopping all')
    stopPolling()
    Object.values(_retryTimers).forEach(t => clearTimeout(t))
    Object.values(_offerRetryTimers).forEach(t => clearTimeout(t))
    Object.keys(_peers).forEach(id => {
      try { _peers[id].close() } catch { /* ok */ }
      delete _peers[id]
    })
    remoteStreams.value = {}
    connectionStates.value = {}
    if (localStream.value) {
      localStream.value.getTracks().forEach(t => t.stop())
      localStream.value = null
    }
    _gameId = null
    _myId = null
  }

  return {
    localStream,
    remoteStreams,
    connectionStates,
    initCamera,
    configure,
    connectToPeer,
    startPolling,
    stopPolling,
    isPolling,
    cleanup,
  }
}
